import { useState } from "react";

const ROWS_PER_PAGE = 10;

const STATUS_STYLES = {
  Active:      "bg-green-100 text-green-800",
  Upcoming:    "bg-blue-100 text-blue-800",
  Expired:     "bg-red-100 text-red-800",
  "Not Active": "bg-gray-100 text-gray-500",
};

const Shimmer = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded ${className}`} />
);

const TableShimmer = () => (
  <>
    {Array.from({ length: 5 }).map((_, i) => (
      <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
        {/* # */}
        <td className="px-4 py-3"><Shimmer className="h-4 w-6" /></td>
        {/* Image */}
        <td className="px-4 py-3"><Shimmer className="w-24 h-14 rounded-lg" /></td>
        {/* Start Date */}
        <td className="px-4 py-3"><Shimmer className="h-4 w-36" /></td>
        {/* End Date */}
        <td className="px-4 py-3"><Shimmer className="h-4 w-36" /></td>
        {/* Description */}
        <td className="px-4 py-3"><Shimmer className="h-4 w-48" /></td>
        {/* Status */}
        <td className="px-4 py-3"><Shimmer className="h-6 w-20 rounded-full" /></td>
      </tr>
    ))}
  </>
);

const BannerTable = ({ banners, loading, onRowClick, dateTemplate, getStatus, onToggleVisible, API_BASE }) => {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("asc");

  const totalPages = Math.max(1, Math.ceil(banners.length / ROWS_PER_PAGE));

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sorted = [...banners].sort((a, b) => {
    if (!sortField) return 0;
    const aVal = a[sortField] ?? "";
    const bVal = b[sortField] ?? "";
    return sortDir === "asc"
      ? String(aVal).localeCompare(String(bVal))
      : String(bVal).localeCompare(String(aVal));
  });

  const paginated = sorted.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortDir === "asc" ? (
      <svg className="w-3.5 h-3.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-3.5 h-3.5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const ThCell = ({ label, field }) => (
    <th
      onClick={field ? () => handleSort(field) : undefined}
      className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-widest whitespace-nowrap
        ${field ? "cursor-pointer hover:text-black select-none" : ""}`}
    >
      <div className="flex items-center gap-1.5">
        {label}
        {field && <SortIcon field={field} />}
      </div>
    </th>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <ThCell label="#" />
              <ThCell label="Image" />
              <ThCell label="Start Date" field="startDate" />
              <ThCell label="End Date" field="endDate" />
              <ThCell label="Description" field="description" />
              <ThCell label="Status" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <TableShimmer />
            ) : paginated.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-gray-400 text-sm">
                  No banners found
                </td>
              </tr>
            ) : (
              paginated.map((banner, idx) => {
                const status = getStatus(banner.startDate, banner.endDate, banner.visible);
                const isDull = status === "Expired" || status === "Not Active";
                return (
                  <tr
                    key={banner._id}
                    onClick={() => onRowClick(banner)}
                    className={`cursor-pointer transition-colors duration-150
                      ${isDull ? "opacity-45 bg-gray-100" : idx % 2 === 0 ? "bg-white hover:bg-gray-50" : "bg-gray-50/50 hover:bg-gray-100"}`}
                  >
                    <td className="px-4 py-3 text-gray-500 text-xs font-medium w-10">
                      {(page - 1) * ROWS_PER_PAGE + idx + 1}
                    </td>
                    <td className="px-4 py-3">
                      <img
                        src={`${API_BASE}/${banner.imagePath}`}
                        alt="Banner"
                        loading="lazy"
                        className="w-24 h-14 object-cover rounded-lg border border-gray-200"
                      />
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {dateTemplate(banner.startDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                      {dateTemplate(banner.endDate)}
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs truncate">
                      {banner.description || "—"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1.5">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[status] || "bg-gray-100 text-gray-600"}`}>
                          {status}
                        </span>
                        {/* Visible toggle */}
                        <button
                          type="button"
                          onClick={(e) => onToggleVisible(banner, e)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none
                            ${banner.visible ? "bg-green-500" : "bg-gray-300"}`}
                        >
                          <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200
                            ${banner.visible ? "translate-x-4" : "translate-x-0.5"}`}
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {banners.length > ROWS_PER_PAGE && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing{" "}
            <span className="font-medium text-gray-700">
              {(page - 1) * ROWS_PER_PAGE + 1}–{Math.min(page * ROWS_PER_PAGE, banners.length)}
            </span>{" "}
            of <span className="font-medium text-gray-700">{banners.length}</span>
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-black hover:text-black disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce((acc, p, i, arr) => {
                if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                acc.push(p);
                return acc;
              }, [])
              .map((item, i) =>
                item === "..." ? (
                  <span key={`ellipsis-${i}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-xs">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setPage(item)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium transition-all
                      ${page === item
                        ? "bg-black text-white"
                        : "border border-gray-200 text-gray-600 hover:border-black hover:text-black"
                      }`}
                  >
                    {item}
                  </button>
                )
              )}

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:border-black hover:text-black disabled:opacity-40 disabled:cursor-not-allowed transition-all"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerTable;
